/**
 * Color Performance Optimizer
 * Optimizes color animations and transitions for better performance
 */

class ColorPerformanceOptimizer {
  constructor() {
    this.animationFrameId = null;
    this.activeAnimations = new Map();
    this.performanceMetrics = {
      frameRate: 60,
      averageFrameTime: 16.67,
      droppedFrames: 0,
      totalFrames: 0
    };
    
    this.init();
  }

  /**
   * Initialize the optimizer
   */
  init() {
    this.setupPerformanceMonitoring();
    this.optimizeExistingAnimations();
    this.setupIntersectionObserver();
  }

  /**
   * Setup performance monitoring
   */
  setupPerformanceMonitoring() {
    let lastFrameTime = performance.now();
    let frameCount = 0;
    let totalFrameTime = 0;

    const monitorFrame = (currentTime) => {
      const frameTime = currentTime - lastFrameTime;
      lastFrameTime = currentTime;
      frameCount++;
      totalFrameTime += frameTime;

      // Calculate metrics every 60 frames
      if (frameCount >= 60) {
        this.performanceMetrics.averageFrameTime = totalFrameTime / frameCount;
        this.performanceMetrics.frameRate = 1000 / this.performanceMetrics.averageFrameTime;
        
        // Count dropped frames (frames taking longer than 16.67ms)
        if (frameTime > 16.67) {
          this.performanceMetrics.droppedFrames++;
        }
        
        this.performanceMetrics.totalFrames += frameCount;
        
        // Reset counters
        frameCount = 0;
        totalFrameTime = 0;
        
      }

      this.animationFrameId = requestAnimationFrame(monitorFrame);
    };

    this.animationFrameId = requestAnimationFrame(monitorFrame);
  }


  /**
   * Apply aggressive optimizations for poor performance
   */
  applyAggressiveOptimizations() {
    document.documentElement.classList.add('performance-mode-aggressive');
    
    // Disable complex animations
    this.disableComplexAnimations();
    
    // Reduce gradient complexity
    this.simplifyGradients();
    
    // Disable backdrop filters
    this.disableBackdropFilters();
    
    console.log('Applied aggressive performance optimizations');
  }

  /**
   * Apply standard optimizations for moderate performance
   */
  applyStandardOptimizations() {
    console.log('Applied standard performance optimizations');
  }

  /**
   * Optimize existing animations
   */
  optimizeExistingAnimations() {
    const animatedElements = document.querySelectorAll('[class*="transition"], [class*="animation"]');
    
    animatedElements.forEach(element => {
      this.optimizeElement(element);
    });
  }

  /**
   * Optimize a specific element
   */
  optimizeElement(element) {
    // Add will-change property for elements that will animate
    if (this.willAnimate(element)) {
      element.style.willChange = 'transform, opacity';
      
      // Remove will-change after animation completes
      element.addEventListener('transitionend', () => {
        element.style.willChange = 'auto';
      });
      
      element.addEventListener('animationend', () => {
        element.style.willChange = 'auto';
      });
    }

    // Use transform3d to enable hardware acceleration
    if (this.needsHardwareAcceleration(element)) {
      element.style.transform = element.style.transform || 'translate3d(0, 0, 0)';
      element.style.backfaceVisibility = 'hidden';
      element.style.perspective = '1000px';
    }

    // Optimize color transitions
    this.optimizeColorTransitions(element);
  }

  /**
   * Check if element will animate
   */
  willAnimate(element) {
    const computedStyle = getComputedStyle(element);
    const transition = computedStyle.transition;
    const animation = computedStyle.animation;
    
    return transition !== 'none' || animation !== 'none';
  }

  /**
   * Check if element needs hardware acceleration
   */
  needsHardwareAcceleration(element) {
    const computedStyle = getComputedStyle(element);
    const transform = computedStyle.transform;
    const opacity = computedStyle.opacity;
    
    return transform !== 'none' || opacity !== '1';
  }

  /**
   * Optimize color transitions for an element
   */
  optimizeColorTransitions(element) {
    const computedStyle = getComputedStyle(element);
    const transition = computedStyle.transition;
    
    if (transition.includes('background-color') || transition.includes('color')) {
      // Use shorter transition durations for better performance
      const optimizedTransition = transition.replace(/(\d+\.?\d*)s/g, (match, duration) => {
        const newDuration = Math.min(parseFloat(duration), 0.3);
        return `${newDuration}s`;
      });
      
      element.style.transition = optimizedTransition;
    }
  }

  /**
   * Disable complex animations
   */
  disableComplexAnimations() {
    const style = document.createElement('style');
    style.textContent = `
      .performance-mode-aggressive * {
        animation-duration: 0.1s !important;
        transition-duration: 0.1s !important;
      }
      
      .performance-mode-aggressive .gradient-animation,
      .performance-mode-aggressive .complex-animation {
        animation: none !important;
      }
    `;
    document.head.appendChild(style);
  }

  /**
   * Simplify gradients for better performance
   */
  simplifyGradients() {
    const style = document.createElement('style');
    style.textContent = `
      .performance-mode-aggressive .bg-gradient-primary {
        background: var(--color-primary-blue-500) !important;
      }
      
      .performance-mode-aggressive .bg-gradient-secondary {
        background: var(--color-primary-teal-500) !important;
      }
      
      .performance-mode-aggressive .bg-gradient-accent {
        background: var(--color-accent-orange-500) !important;
      }
      
      .performance-mode-aggressive .text-gradient-primary,
      .performance-mode-aggressive .text-gradient-secondary,
      .performance-mode-aggressive .text-gradient-accent {
        background: none !important;
        color: var(--text-accent) !important;
        -webkit-background-clip: unset !important;
        -webkit-text-fill-color: unset !important;
      }
    `;
    document.head.appendChild(style);
  }

  /**
   * Disable backdrop filters
   */
  disableBackdropFilters() {
    const style = document.createElement('style');
    style.textContent = `
      .performance-mode-aggressive .glass-effect {
        backdrop-filter: none !important;
        -webkit-backdrop-filter: none !important;
        background: rgba(255, 255, 255, 0.1) !important;
      }
    `;
    document.head.appendChild(style);
  }

  /**
   * Reduce animation duration
   */
  reduceAnimationDuration() {
    const style = document.createElement('style');
    document.head.appendChild(style);
  }

  /**
   * Limit concurrent animations
   */
  limitConcurrentAnimations() {
    let activeAnimationCount = 0;
    const maxConcurrentAnimations = 3;

    document.addEventListener('animationstart', () => {
      activeAnimationCount++;
      if (activeAnimationCount > maxConcurrentAnimations) {
        // Pause new animations if too many are running
        document.documentElement.classList.add('pause-new-animations');
      }
    });

    document.addEventListener('animationend', () => {
      activeAnimationCount--;
      if (activeAnimationCount <= maxConcurrentAnimations) {
        document.documentElement.classList.remove('pause-new-animations');
      }
    });
  }

  /**
   * Setup intersection observer to pause animations for off-screen elements
   */
  setupIntersectionObserver() {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          // Element is visible, enable animations
          entry.target.classList.remove('paused-animation');
        } else {
          // Element is not visible, pause animations
          entry.target.classList.add('paused-animation');
        }
      });
    }, {
      rootMargin: '50px' // Start animations 50px before element comes into view
    });

    // Observe all animated elements
    const animatedElements = document.querySelectorAll('[class*="animation"], [class*="transition"]');
    animatedElements.forEach(element => {
      observer.observe(element);
    });

    // Add styles for paused animations
    const style = document.createElement('style');
    style.textContent = `
      .paused-animation {
        animation-play-state: paused !important;
      }
    `;
    document.head.appendChild(style);
  }

  /**
   * Optimize gradient rendering
   */
  optimizeGradientRendering() {
    // Use CSS containment for gradient elements
    const gradientElements = document.querySelectorAll('[class*="gradient"]');
    
    gradientElements.forEach(element => {
      element.style.contain = 'layout style paint';
    });
  }

  /**
   * Batch color updates for better performance
   */
  batchColorUpdates(updates) {
    // Use requestAnimationFrame to batch DOM updates
    requestAnimationFrame(() => {
      updates.forEach(update => {
        const { element, property, value } = update;
        element.style[property] = value;
      });
    });
  }

  /**
   * Preload critical colors
   */
  preloadCriticalColors() {
    const criticalColors = [
      '--color-primary-blue-500',
      '--color-primary-purple-500',
      '--color-primary-teal-500',
      '--bg-primary',
      '--text-primary'
    ];

    // Force browser to compute these colors early
    const testElement = document.createElement('div');
    testElement.style.position = 'absolute';
    testElement.style.visibility = 'hidden';
    testElement.style.pointerEvents = 'none';
    
    criticalColors.forEach(color => {
      testElement.style.backgroundColor = `var(${color})`;
    });
    
    document.body.appendChild(testElement);
    
    // Force computation
    // eslint-disable-next-line no-unused-expressions
    getComputedStyle(testElement).backgroundColor;
    
    // Clean up
    document.body.removeChild(testElement);
  }

  /**
   * Get performance metrics
   */
  getPerformanceMetrics() {
    return {
      ...this.performanceMetrics,
      timestamp: Date.now(),
      activeAnimations: this.activeAnimations.size
    };
  }

  /**
   * Clean up resources
   */
  cleanup() {
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }
    
    this.activeAnimations.clear();
  }
}

// Export for use in other modules
export default ColorPerformanceOptimizer;

// Auto-initialize optimizer
export const colorPerformanceOptimizer = new ColorPerformanceOptimizer();

// Preload critical colors
colorPerformanceOptimizer.preloadCriticalColors();

// Log performance metrics in development
if (process.env.NODE_ENV === 'development') {
  setInterval(() => {
    console.log('Color Performance Metrics:', colorPerformanceOptimizer.getPerformanceMetrics());
  }, 5000);
}